import { datePl, type DemandDto, type DemandEventDto, type MeDto, type ShortageByItemDto, shiftDate, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Button, Card, Drawer, Group, SegmentedControl, Select, Stack, Table, Text, TextInput, Timeline } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconClipboardCheck, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { DemandSourceBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useWarehouses } from '../../data';
import { formatDateTime, plural, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { whLabel } from './common';
import { DemandHeader, DemandLinesTable, useDemandRefresh } from './demand-ui';
import { ExtraMaterialModal } from './ExtraMaterialModal';

const VIEWS = [
  { value: 'otwarte', label: 'Wg terminów' },
  { value: 'braki', label: 'Braki wg produktu' },
  { value: 'zmienione', label: 'Nowe i zmienione' },
  { value: 'do_dopasowania', label: 'Do dopasowania' },
];

/** Przedział terminu: po terminie, dziś, jutro, ten tydzień, później, bez terminu. */
function bucket(d: string | null): string {
  if (!d) return 'Bez terminu';
  const today = todayInWarsaw();
  if (d < today) return 'Po terminie';
  if (d === today) return 'Dziś';
  if (d === shiftDate(today, 1)) return 'Jutro';
  if (d <= shiftDate(today, 7)) return 'Najbliższe 7 dni';
  return 'Później';
}
const BUCKETS = ['Po terminie', 'Dziś', 'Jutro', 'Najbliższe 7 dni', 'Później', 'Bez terminu'];

/** Zapotrzebowania (docs/14 §3): potrzeby z umów i zgłoszenia ekip razem, z podziałem na terminy i braki. */
export function DemandsPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const view = params.get('widok') ?? 'otwarte';
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 300);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const openId = params.get('zapotrzebowanie');
  const whs = useWarehouses(can(me, 'magazyn.podglad'));
  const setParam = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const qs = new URLSearchParams({ view: view === 'braki' ? 'otwarte' : view });
  if (debounced.trim()) qs.set('q', debounced.trim());
  if (warehouseId) qs.set('warehouseId', warehouseId);
  const list = useQuery({ queryKey: ['demands', qs.toString()], queryFn: () => api<DemandDto[]>(`/demands?${qs}`), enabled: view !== 'braki' });
  const shortages = useQuery({ queryKey: ['shortages', warehouseId], queryFn: () => api<ShortageByItemDto[]>(`/demands/shortages${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), enabled: view === 'braki' });
  const groups = BUCKETS.map((b) => ({ b, items: (list.data ?? []).filter((d) => bucket(d.neededBy) === b) })).filter((g) => g.items.length);

  return (
    <>
      <PageHeader
        title="Zapotrzebowania"
        description="Potrzeby z umów i zgłoszenia ekip w jednym miejscu. Rezerwacja zabezpiecza towar, ale nie zmniejsza stanu — dopiero wydanie."
      />
      <Group mb="md" wrap="wrap">
        <SegmentedControl data={VIEWS} value={view} onChange={(v) => setParam('widok', v === 'otwarte' ? null : v)} />
        <TextInput placeholder="Szukaj: numer lub tytuł sprawy…" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={{ base: '100%', sm: 260 }} />
        {(whs.data?.length ?? 0) > 1 && (
          <Select placeholder="Wszystkie magazyny" clearable data={(whs.data ?? []).map((w) => ({ value: w.id, label: whLabel(w) }))} value={warehouseId} onChange={setWarehouseId} w={{ base: '100%', sm: 260 }} />
        )}
        {view === 'braki' && can(me, 'zakupy.zamowienia') && (
          <Button component={Link} to="/magazyn/zamowienia?widok=podpowiedzi" variant="light" ml="auto">
            Zamów braki (podpowiedzi zakupowe)
          </Button>
        )}
      </Group>
      {view === 'braki' ? (
        <Card padding={0}>
          <QueryState isLoading={shortages.isLoading} error={shortages.error}>
            {shortages.data?.length === 0 ? (
              <EmptyState icon={<IconClipboardCheck size={24} />} title="Brak braków" hint="Wszystkie otwarte potrzeby są zarezerwowane albo wydane." />
            ) : (
              <Table.ScrollContainer minWidth={760}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Produkt</Table.Th>
                      <Table.Th>Magazyn</Table.Th>
                      <Table.Th ta="right">Brak</Table.Th>
                      <Table.Th ta="right">Dostępne</Table.Th>
                      <Table.Th>Najwcześniej potrzebne</Table.Th>
                      <Table.Th>Dla</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {shortages.data?.map((s) => (
                      <Table.Tr key={`${s.warehouseId}-${s.itemId}`}>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {s.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {s.code}
                          </Text>
                        </Table.Td>
                        <Table.Td>{s.warehouseCode}</Table.Td>
                        <Table.Td ta="right">
                          <Text fw={700} c="orange">
                            {qtyPl(s.shortage)} {s.unit}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">{qtyPl(s.available)}</Table.Td>
                        <Table.Td>{s.earliestNeededBy ? datePl(s.earliestNeededBy) : 'bez terminu'}</Table.Td>
                        <Table.Td>
                          <Stack gap={2}>
                            {s.lines.map((l) => (
                              <Group key={l.lineId} gap={4} wrap="nowrap">
                                <Anchor size="xs" onClick={() => setParam('zapotrzebowanie', l.demandId)}>
                                  {l.caseNumber ?? 'zapas'}
                                </Anchor>
                                <Text size="xs" c="dimmed">
                                  {qtyPl(l.shortage)} · {l.neededBy ? datePl(l.neededBy) : 'bez terminu'}
                                  {l.taskNumber ? ` · ${l.taskNumber}` : ''}
                                </Text>
                                <DemandSourceBadge source={l.source} size="xs" />
                              </Group>
                            ))}
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </QueryState>
        </Card>
      ) : (
        <QueryState isLoading={list.isLoading} error={list.error}>
          {!list.data?.length ? (
            <Card>
              <EmptyState icon={<IconClipboardCheck size={24} />} title="Brak zapotrzebowań w tym widoku" />
            </Card>
          ) : (
            <Stack>
              {groups.map((g) => (
                <SectionCard key={g.b} title={`${g.b} (${g.items.length})`} padding={0}>
                  <Table highlightOnHover>
                    <Table.Tbody>
                      {g.items.map((d) => {
                        const short = d.lines.filter((l) => l.shortage > 0).length;
                        return (
                          <Table.Tr key={d.id} className="row-link" onClick={() => setParam('zapotrzebowanie', d.id)}>
                            <Table.Td>
                              <DemandHeader d={d} linkCase={false} />
                              <Text size="xs" c="dimmed" mt={2}>
                                {d.caseTitle ?? ''} · {d.companyCode} {d.warehouseCode} · {d.lines.length} {plural(d.lines.length, 'pozycja', 'pozycje', 'pozycji')}
                                {short ? ` · braki: ${short}` : ''}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </SectionCard>
              ))}
            </Stack>
          )}
        </QueryState>
      )}
      <DemandDrawer id={openId} onClose={() => setParam('zapotrzebowanie', null)} me={me} />
    </>
  );
}

const EVENT_LABELS: Record<string, string> = {
  utworzenie: 'Utworzono z umowy',
  pozycja: 'Dodano pozycję',
  zgloszenie: 'Zgłoszenie ekipy',
  zmiana: 'Zmiana ilości',
  zmiana_umowy: 'Zmiana z umowy (aneks, nowa wersja)',
  anulowanie: 'Anulowanie',
  dopasowanie: 'Dopasowano do produktu',
  przejrzane: 'Przejrzane przez magazyn',
  termin: 'Zmiana terminu gotowości',
  przeniesienie: 'Przeniesiono rezerwację na tę pozycję',
  utrata: 'Rezerwację przeniesiono na inną realizację',
  zmiana_oczekujaca: 'Zmiana od ekipy po kompletacji (do decyzji)',
  zmiana_odrzucona: 'Zmiana od ekipy odrzucona',
  ryzyko: 'Termin zagrożony',
  zwrot: 'Zwrot nadmiaru do magazynu',
  zamkniecie: 'Sprawa zamknięta — niewydane pozycje anulowane',
  zrealizowanie: 'Zrealizowane (wydane) — zamknięte ze sprawą',
  zadanie_anulowane: 'Zadanie anulowane',
  wydane_wczesniej: 'Uwzględniono wydania z poprzedniej wersji umowy',
};

function describe(e: DemandEventDto): string {
  const d = e.data as Record<string, unknown>;
  if (e.kind === 'termin') return `${d.przed ? datePl(String(d.przed)) : 'brak'} → ${d.po ? datePl(String(d.po)) : 'brak'}`;
  if ('przed' in d && 'po' in d) return `${qtyPl(Number(d.przed))} → ${qtyPl(Number(d.po))}`;
  if (e.kind === 'dopasowanie') return `„${String(d.opis ?? '')}” → ${String(d.produkt ?? '')}`;
  if ('ilosc' in d) return qtyPl(Number(d.ilosc));
  return '';
}

function DemandDrawer({ id, onClose, me }: { id: string | null; onClose: () => void; me: MeDto }) {
  const q = useQuery({ queryKey: ['demand', id], queryFn: () => api<DemandDto & { events: DemandEventDto[] }>(`/demands/${id}`), enabled: !!id });
  const refresh = useDemandRefresh();
  const manage = can(me, 'magazyn.rezerwacje');
  const reviewed = useMutation({
    mutationFn: () => api(`/demands/${id}/reviewed`, { method: 'POST' }),
    onSuccess: () => (notifyOk('Oznaczono jako przejrzane'), refresh()),
    onError: notifyError,
  });
  const [extra, setExtra] = useState(false);
  const [neededBy, setNeededBy] = useState<string>('');
  const setTerm = useMutation({
    mutationFn: () => api(`/demands/${id}/needed-by`, { method: 'PUT', body: { neededBy: neededBy || null } }),
    onSuccess: () => (notifyOk('Zapisano termin'), refresh()),
    onError: notifyError,
  });
  const d = q.data;
  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="xl" title={d ? `Zapotrzebowanie ${d.caseNumber ?? ''}` : 'Zapotrzebowanie'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <Stack>
            <DemandHeader d={d} linkCase={can(me, 'sprawy.podglad')} />
            {d.caseTitle && (
              <Text size="sm" c="dimmed">
                {d.caseTitle}
              </Text>
            )}
            {d.comment && (
              <Alert variant="light" color="gray" p="xs">
                {d.requestedByName ? `${d.requestedByName}: ` : ''}
                {d.comment}
              </Alert>
            )}
            {d.photoIds.length > 0 && (
              <Text size="xs" c="dimmed">
                Zdjęcia od ekipy: {d.photoIds.length} (w zadaniu {d.taskNumber})
              </Text>
            )}
            {d.unreviewed && manage && (
              <Group>
                <Button size="xs" onClick={() => reviewed.mutate()} loading={reviewed.isPending}>
                  Oznacz jako przejrzane
                </Button>
              </Group>
            )}
            <DemandLinesTable d={d} manage={manage && d.status === 'otwarte'} warehouseId={d.warehouseId} />
            {manage && (!d.neededBySource || d.neededBySource === 'reczny') && d.status === 'otwarte' && (
              <Group gap="xs" align="flex-end">
                <TextInput size="xs" type="date" label="Termin gotowości (ręcznie, do czasu zaplanowania zadania)" value={neededBy || d.neededBy || ''} onChange={(e) => setNeededBy(e.currentTarget.value)} />
                <Button size="xs" variant="light" onClick={() => setTerm.mutate()} loading={setTerm.isPending} disabled={!neededBy || neededBy === d.neededBy}>
                  Ustaw
                </Button>
              </Group>
            )}
            <Group gap="md">
              {d.caseId && can(me, 'sprawy.podglad') && (
                <Anchor component={Link} to={`/sprawy/${d.caseId}?zakladka=materialy`} size="sm">
                  Materiały sprawy →
                </Anchor>
              )}
              {d.taskId && d.status === 'otwarte' && can(me, 'magazyn.kompletacja') && (
                <Anchor component={Link} to={`/magazyn/pakiety?nowy=${d.taskId}`} size="sm">
                  Przygotuj pakiet →
                </Anchor>
              )}
              {/* QA 11.2026 P3: magazyn dopisuje brakujący materiał do zadania bez karty sprawy (także pozycję spoza katalogu) */}
              {d.taskId && d.status === 'otwarte' && can(me, 'zapotrzebowania.zglaszanie') && (
                <Anchor component="button" type="button" size="sm" onClick={() => setExtra(true)}>
                  Dopisz materiał do zadania →
                </Anchor>
              )}
              {d.lines.some((l) => l.shortage > 0) && can(me, 'zakupy.zamowienia') && (
                <Anchor component={Link} to="/magazyn/zamowienia?widok=podpowiedzi" size="sm">
                  Zamów brakujące →
                </Anchor>
              )}
            </Group>
            {extra && d.taskId && <ExtraMaterialModal task={{ id: d.taskId, label: `${d.taskNumber ?? ''}${d.caseNumber ? ` · ${d.caseNumber}` : ''}` }} onClose={() => setExtra(false)} />}
            <SectionCard title="Historia">
              <Timeline bulletSize={10} lineWidth={2}>
                {d.events.map((e) => (
                  <Timeline.Item key={e.id} title={<Text size="sm">{EVENT_LABELS[e.kind] ?? e.kind}{e.lineName ? ` — ${e.lineName}` : ''}</Text>}>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(e.at)} · {e.userName ?? 'system'}
                      {describe(e) ? ` · ${describe(e)}` : ''}
                      {e.reason ? ` · ${e.reason}` : ''}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </SectionCard>
          </Stack>
        )}
      </QueryState>
    </Drawer>
  );
}

